// KPI içe aktarma sihirbazı — saf mantık (M16 T11; kural 7-8 triage, 25 satır/sayfa).
// Bileşenler yalnız render eder; filtre/sayfalama/karar/diff özeti/türetilmiş önizleme burada
// testlidir. Kopya `kpi-import-copy.ts`'te.
// güvenlik/UX taraması 2026-10-08 (600 satır sınırı): varsayılan yıl/işletme kararları
// `kpi-import-wizard-state.ts`, önizleme diff/türetilmiş hesap/hata mesajları
// `kpi-import-wizard-plan.ts` — bu modül re-export eder, tüketiciler ve testler değişmez.

import {
  formatKpiNumber,
  type KpiImportColumn,
  type KpiImportGrid,
  type KpiImportRow,
  type KpiMappingAction,
  type KpiMappingRow,
  type KpiSetIndicator,
} from "@mahir/shared";
import type { KpiImportFileKind } from "../api/kpi-import";
import { breakdownProblem } from "./kpi-import-breakdown";
import { KPI_IMPORT_COPY } from "./kpi-import-copy";

export * from "./kpi-import-wizard-plan";
export * from "./kpi-import-wizard-state";

export const KPI_IMPORT_PAGE_SIZE = 25;
export const KPI_IMPORT_MAX_BYTES = 50 * 1024 * 1024;
/** Büyük dosya uyarısı eşiği (yükleme/okuma uzun sürer). */
export const KPI_IMPORT_LARGE_BYTES = 15 * 1024 * 1024;
/** mapping-llm `KPI_MAPPING_AUTO_THRESHOLD` ile hizalı (≥0.8 otomatik öneri). */
export const KPI_IMPORT_AUTO_THRESHOLD = 0.8;
export const KPI_IMPORT_UNCERTAIN_THRESHOLD = 0.6;

// ─── Dosya ───────────────────────────────────────────────────────────────────

export function kpiImportFileKind(file: { name: string }): KpiImportFileKind | "xls" | null {
  const ext = file.name.toLowerCase().split(".").pop() ?? "";
  if (ext === "xlsx") return "xlsx";
  if (ext === "pdf") return "pdf";
  if (ext === "docx") return "docx";
  if (ext === "xls") return "xls";
  return null;
}

/** FileDropzone `rejectReason` — null = kabul. */
export function kpiImportRejectReason(file: { name: string; size: number }): string | null {
  const kind = kpiImportFileKind(file);
  if (kind === "xls") return KPI_IMPORT_COPY.upload.rejectXls;
  if (kind === null) return KPI_IMPORT_COPY.upload.rejectType;
  if (file.size > KPI_IMPORT_MAX_BYTES) return KPI_IMPORT_COPY.upload.rejectSize;
  return null;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n.toString()} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

// ─── Güven / durum ──────────────────────────────────────────────────────────

export type ConfidenceBand = "high" | "medium" | "low" | "none";

export function confidenceBand(c: number | undefined): ConfidenceBand {
  if (c === undefined) return "none";
  if (c >= KPI_IMPORT_AUTO_THRESHOLD) return "high";
  if (c >= KPI_IMPORT_UNCERTAIN_THRESHOLD) return "medium";
  return "low";
}

export type MappingRowStatus = "matched" | "uncertain" | "unmatched" | "skipped";

/**
 * Triage durumu (kural 7): skip → atlandı; hedefsiz map/create → eşleşmedi; hedefli ve
 * (güven ≥0.8 | hafıza | elle) → eşleşti; hedefli düşük güven → belirsiz.
 */
export function mappingRowStatus(row: KpiMappingRow): MappingRowStatus {
  if (row.action === "skip") return "skipped";
  // Kırılım satırının "hedefi" üç alanıdır (Üst gösterge / Grup / Üye adı); eksikse eşleşmedi
  const hasTarget =
    row.action === "create_breakdown" ? breakdownProblem(row.breakdown) === null : !!row.targetCode;
  if (!hasTarget) return "unmatched";
  if (row.memoryHit || row.confidence === undefined) return "matched";
  return row.confidence >= KPI_IMPORT_AUTO_THRESHOLD ? "matched" : "uncertain";
}

export function isPendingDecision(row: KpiMappingRow): boolean {
  const s = mappingRowStatus(row);
  return s === "uncertain" || s === "unmatched";
}

/**
 * Toplu kabule giren satır: hedefli, ≥0.8 güven VE firma geneli hafıza isabeti DEĞİL (B-04 —
 * `memoryHit` + güven <1 firma geneli kayıttır; eşleşti sayılır ama kullanıcı onayı ister).
 */
export function isAutoAcceptable(row: KpiMappingRow): boolean {
  if (row.action === "skip" || !row.targetCode || row.confidence === undefined) return false;
  if (row.confidence < KPI_IMPORT_AUTO_THRESHOLD) return false;
  if (row.memoryHit && row.confidence < 1) return false;
  return true;
}

export interface MappingCounts {
  total: number;
  matched: number;
  uncertain: number;
  unmatched: number;
  skipped: number;
  pending: number;
  highConfidence: number;
}

export function countMapping(rows: readonly KpiMappingRow[]): MappingCounts {
  const c: MappingCounts = {
    total: rows.length,
    matched: 0,
    uncertain: 0,
    unmatched: 0,
    skipped: 0,
    pending: 0,
    highConfidence: 0,
  };
  for (const r of rows) {
    const s = mappingRowStatus(r);
    c[s] += 1;
    if (s === "uncertain" || s === "unmatched") c.pending += 1;
    if (isAutoAcceptable(r)) c.highConfidence += 1;
  }
  return c;
}

// ─── Başlangıç eşlemesi / karar ──────────────────────────────────────────────

/** Grid sırasında tam satır listesi; sunucu önerisi olmayan satır `skip` olarak gelir. */
export function initialMapping(
  grid: KpiImportGrid,
  suggested: readonly KpiMappingRow[] | null,
): KpiMappingRow[] {
  const byId = new Map((suggested ?? []).map((m) => [m.rowId, m]));
  const out: KpiMappingRow[] = [];
  for (const sheet of grid.sheets) {
    for (const row of sheet.rows) {
      const s = byId.get(row.rowId);
      out.push(
        s
          ? { ...s, rowLabel: row.label }
          : { rowId: row.rowId, rowLabel: row.label, action: "skip" },
      );
    }
  }
  return out;
}

/**
 * rowId öneki = çalışma kitabındaki sayfa sırası (gizli sayfalar dahil, kimlik kaymasın) — grid dizi
 * indeksiyle AYNI DEĞİL (Lila: gizli sayfa 0 → görünür sayfa rowId "1:x" ama `grid.sheets[0]`).
 * Sayfa sekmesi/kolon modeli/sayfa bazlı yıl hep grid dizi indeksiyle çalışır → `sheetIndexByRowId`.
 */
export function rowSheetIndex(rowId: string): number {
  const n = Number(rowId.split(":")[0]);
  return Number.isFinite(n) ? n : 0;
}

/** rowId → grid dizi indeksi (sayfa sekmesi, kolon modeli ve sayfa bazlı yıl bu indeksle anahtarlanır). */
export function sheetIndexByRowId(grid: KpiImportGrid): Map<string, number> {
  const out = new Map<string, number>();
  grid.sheets.forEach((s, i) => {
    for (const r of s.rows) out.set(r.rowId, i);
  });
  return out;
}

/** Kullanıcı kararı: güven/hafıza izi düşer, not "elle seçildi". `targetCode` null → silinir. */
export function decideRow(
  row: KpiMappingRow,
  patch: { action?: KpiMappingAction; targetCode?: string | null },
): KpiMappingRow {
  const action = patch.action ?? row.action;
  const target = patch.targetCode === undefined ? row.targetCode : (patch.targetCode ?? undefined);
  const next: KpiMappingRow = {
    rowId: row.rowId,
    rowLabel: row.rowLabel,
    action,
    note: KPI_IMPORT_COPY.mapping.manualNote,
  };
  if (action === "create_breakdown" || action === "skip") {
    // kırılım alanları korunur: atlanan öneri tabloda "Atlandı" kalır, geri alınabilir
    if (row.breakdown) next.breakdown = row.breakdown;
  } else if (target) next.targetCode = target;
  return next;
}

export function acceptHighConfidence(rows: readonly KpiMappingRow[]): KpiMappingRow[] {
  return rows.map((r) => (isAutoAcceptable(r) ? decideRow(r, {}) : r));
}

// ─── Hedef seçenekleri + müşteri modu (P2-9, P1-2/P1-3) ─────────────────────

export interface TargetOptionLike {
  code: string;
  label: string;
  unit: string | null;
  isDerived: boolean;
}

/** Çocuğu olan set göstergeleri (kategori/bant) — hedef olamaz (P2-9). */
export function categoryCodesOf(
  indicators: readonly Pick<KpiSetIndicator, "id" | "code" | "parentId">[],
): Set<string> {
  const parents = new Set(indicators.map((i) => i.parentId).filter((p): p is string => !!p));
  return new Set(indicators.filter((i) => parents.has(i.id)).map((i) => i.code));
}

/**
 * Hedef seçici listesi: kategori düğümleri düşer; müşteri modunda yalnız `open` göstergeler kalır
 * (salt okunur/kapalı hedef seçilemez — P1-2).
 */
export function targetOptionsFrom(
  indicators: readonly KpiSetIndicator[],
  opts: { isClient: boolean },
): TargetOptionLike[] {
  const categories = categoryCodesOf(indicators);
  return indicators
    .filter((i) => !categories.has(i.code))
    .filter((i) => !opts.isClient || i.effectiveClientAccess === "open")
    .map((i) => ({ code: i.code, label: i.label, unit: i.unit, isDerived: i.isDerived }));
}

/**
 * Müşteri modunda öneri temizliği (P1-2/P1-3): hedefi seçilebilir listede olmayan map/reported
 * satırı → skip + "salt okunur gösterge"; `create` önerisi → skip + "yeni gösterge eklenemez".
 * Değişiklik yoksa aynı diziyi döndürür (referans eşitliği — tekrar kaydetmez).
 */
export function sanitizeClientMapping(
  rows: readonly KpiMappingRow[],
  allowedCodes: ReadonlySet<string>,
): KpiMappingRow[] {
  let changed = false;
  const out = rows.map((r) => {
    if (r.action === "skip") return r;
    // C3 — kırılım önerisi müşteriye kapalı (sunucu zaten tek genel gerekçeyle düşürür; savunma)
    if (r.action === "create_breakdown") {
      changed = true;
      return {
        rowId: r.rowId,
        rowLabel: r.rowLabel,
        action: "skip" as const,
        note: KPI_IMPORT_COPY.client.closedTargetNote,
      };
    }
    if (r.action === "create") {
      changed = true;
      return {
        rowId: r.rowId,
        rowLabel: r.rowLabel,
        action: "skip" as const,
        note: KPI_IMPORT_COPY.client.createNotAllowedNote,
      };
    }
    if (r.targetCode && !allowedCodes.has(r.targetCode)) {
      changed = true;
      return {
        rowId: r.rowId,
        rowLabel: r.rowLabel,
        action: "skip" as const,
        note: KPI_IMPORT_COPY.client.readonlyNote,
      };
    }
    return r;
  });
  return changed ? out : [...rows];
}

// ─── Varsayılan yıl / işletme (B-02/B-03) → kpi-import-wizard-state.ts (2026-10-08 ayrımı) ──

export function skipRows(
  rows: readonly KpiMappingRow[],
  ids: ReadonlySet<string>,
): KpiMappingRow[] {
  return rows.map((r) => (ids.has(r.rowId) ? decideRow(r, { action: "skip" }) : r));
}

// ─── Filtre / sıralama / sayfalama ──────────────────────────────────────────

export type MappingStatusFilter = "pending" | MappingRowStatus | "all";

export interface MappingFilter {
  /** Grid dizi indeksi (rowId öneki DEĞİL — bkz. `sheetIndexByRowId`). */
  sheet: number | null;
  status: MappingStatusFilter;
  category: string | null;
  search: string;
}

export const DEFAULT_MAPPING_FILTER: MappingFilter = {
  sheet: null,
  status: "pending",
  category: null,
  search: "",
};

const TRIAGE_ORDER: Record<MappingRowStatus, number> = {
  unmatched: 0,
  uncertain: 1,
  matched: 2,
  skipped: 3,
};

/** Varsayılan sıralama (kural 7): eşleşmeyen + düşük güven üstte; grup içinde kararlı. */
export function sortForTriage(rows: readonly KpiMappingRow[]): KpiMappingRow[] {
  return rows
    .map((r, i) => ({ r, i, s: TRIAGE_ORDER[mappingRowStatus(r)] }))
    .sort((a, b) => a.s - b.s || a.i - b.i)
    .map((x) => x.r);
}

function fold(s: string): string {
  return s.toLocaleLowerCase("tr").normalize("NFKC");
}

export function filterMappingRows(
  rows: readonly KpiMappingRow[],
  filter: MappingFilter,
  ctx: {
    categoryByCode: ReadonlyMap<string, string>;
    labelByCode: ReadonlyMap<string, string>;
    /** rowId → grid dizi indeksi; verilmezse rowId önekine düşer (eski davranış). */
    sheetOfRow?: ReadonlyMap<string, number>;
  },
): KpiMappingRow[] {
  const q = fold(filter.search.trim());
  const sheetOf = (rowId: string): number => ctx.sheetOfRow?.get(rowId) ?? rowSheetIndex(rowId);
  return rows.filter((r) => {
    if (filter.sheet !== null && sheetOf(r.rowId) !== filter.sheet) return false;
    if (filter.status !== "all") {
      const s = mappingRowStatus(r);
      if (
        filter.status === "pending"
          ? !(s === "uncertain" || s === "unmatched")
          : s !== filter.status
      )
        return false;
    }
    if (filter.category !== null) {
      if (!r.targetCode || ctx.categoryByCode.get(r.targetCode) !== filter.category) return false;
    }
    if (q) {
      const hay = fold(
        `${r.rowLabel} ${r.targetCode ?? ""} ${r.targetCode ? (ctx.labelByCode.get(r.targetCode) ?? "") : ""}`,
      );
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export interface Page<T> {
  items: T[];
  page: number;
  pages: number;
  from: number;
  to: number;
  total: number;
}

export function paginate<T>(
  items: readonly T[],
  page: number,
  size = KPI_IMPORT_PAGE_SIZE,
): Page<T> {
  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const p = Math.min(Math.max(0, page), pages - 1);
  const from = total === 0 ? 0 : p * size + 1;
  const to = Math.min(total, (p + 1) * size);
  return { items: items.slice(p * size, (p + 1) * size), page: p, pages, from, to, total };
}

// ─── Kategori dizini (set göstergeleri → kök kategori) ──────────────────────

export interface CategoryIndex {
  categories: { id: string; label: string }[];
  categoryByCode: Map<string, string>;
  labelByCode: Map<string, string>;
}

export function buildCategoryIndex(indicators: readonly KpiSetIndicator[]): CategoryIndex {
  const byId = new Map(indicators.map((i) => [i.id, i]));
  const rootOf = (ind: KpiSetIndicator): KpiSetIndicator => {
    let cur = ind;
    let guard = 0;
    while (cur.parentId && guard < 32) {
      const p = byId.get(cur.parentId);
      if (!p) break;
      cur = p;
      guard += 1;
    }
    return cur;
  };
  const categoryByCode = new Map<string, string>();
  const labelByCode = new Map<string, string>();
  const cats = new Map<string, string>();
  for (const ind of indicators) {
    labelByCode.set(ind.code, ind.label);
    const root = rootOf(ind);
    if (root.id !== ind.id || indicators.some((c) => c.parentId === ind.id)) {
      cats.set(root.id, root.label);
      categoryByCode.set(ind.code, root.id);
    }
  }
  return {
    categories: [...cats.entries()].map(([id, label]) => ({ id, label })),
    categoryByCode,
    labelByCode,
  };
}

// ─── Hücre görüntüsü / kolon modeli ─────────────────────────────────────────

export function importCellText(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "number") return formatKpiNumber(v);
  if (typeof v === "boolean") return v ? "Evet" : "Hayır";
  if (typeof v === "string") return v;
  if (v instanceof Date) return v.toLocaleDateString("tr-TR");
  return JSON.stringify(v);
}

/** Satırın örnek değeri: en büyük yıl kolonu, yoksa ilk işletme kolonu, yoksa ilk dolu hücre. */
export function sampleValue(
  row: KpiImportRow,
  columns: readonly KpiImportColumn[],
): { label: string | null; text: string } {
  const years = columns.filter((c) => c.kind === "year" && c.year !== undefined);
  const best = years.sort((a, b) => (b.year ?? 0) - (a.year ?? 0))[0];
  if (best)
    return { label: best.year?.toString() ?? null, text: importCellText(row.cells[best.index]) };
  const ent = columns.find((c) => c.kind === "entity");
  if (ent) return { label: ent.entityName ?? null, text: importCellText(row.cells[ent.index]) };
  const idx = row.cells.findIndex(
    (c, i) => importCellText(c) !== "" && !columns.some((k) => k.index === i && k.kind === "label"),
  );
  return { label: null, text: idx >= 0 ? importCellText(row.cells[idx]) : "" };
}

export function columnsFromGrid(grid: KpiImportGrid): KpiImportColumn[][] {
  return grid.sheets.map((s) => s.columns.map((c) => ({ ...c })));
}

export function columnLetter(index: number): string {
  let n = index + 1;
  let s = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Kolon düzenleme — tür değişince türe ait olmayan alanlar düşer (Zod `optional`). */
export function updateColumn(
  columns: readonly (readonly KpiImportColumn[])[],
  sheetIdx: number,
  colIndex: number,
  patch: Partial<Pick<KpiImportColumn, "kind" | "year" | "entityName">>,
): KpiImportColumn[][] {
  return columns.map((sheet, si) =>
    si !== sheetIdx
      ? [...sheet]
      : sheet.map((c) => {
          if (c.index !== colIndex) return c;
          const kind = patch.kind ?? c.kind;
          const next: KpiImportColumn = { index: c.index, kind };
          const year = patch.year ?? c.year;
          const entityName = patch.entityName ?? c.entityName;
          if (kind === "year" && year !== undefined && Number.isFinite(year)) next.year = year;
          if (kind === "entity" && entityName !== undefined && entityName !== "")
            next.entityName = entityName;
          return next;
        }),
  );
}

/** Hiçbir yıl/işletme kolonu taşımayan sayfalar — bu sayfaların satırları yazılamaz. */
export function sheetsWithoutValueColumns(
  columns: readonly (readonly KpiImportColumn[])[],
): number[] {
  return columns
    .map((sheet, i) => (sheet.some((c) => c.kind === "year" || c.kind === "entity") ? -1 : i))
    .filter((i) => i >= 0);
}

/** Eng 6A: adı mevcut işletmeyle eşleşmeyen işletme kolonları (İleri disabled). */
export function unresolvedEntityNames(
  columns: readonly (readonly KpiImportColumn[])[],
  entities: readonly { name: string }[],
): string[] {
  const known = new Set(entities.map((e) => e.name.trim().toLocaleLowerCase("tr")));
  const out: string[] = [];
  for (const sheet of columns) {
    for (const c of sheet) {
      if (c.kind !== "entity") continue;
      const name = (c.entityName ?? "").trim();
      const key = name === "" ? "(adsız kolon)" : name;
      if (!known.has(name.toLocaleLowerCase("tr")) && !out.includes(key)) out.push(key);
    }
  }
  return out;
}
